import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Divider, Select, TextInput } from '@mantine/core';
import { Check, Link as LinkIcon } from 'tabler-icons-react';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import ResponsiveWebstoreModal from './ResponsiveWebstoreModal';
import AppStack from '../../common/AppStack';
import {
  SOCIAL_SHARING_LIST,
  STORE_HOSTING_URL
} from '../../../config/constants';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';

const renderSelectOption = ({ option, checked }) => (
  <AppFlexbox
    style={{
      flex: 1,
      gap: 8,
      alignItems: 'center',
      justifyContent: 'space-between'
    }}
  >
    <AppFlexbox style={{ flex: 1, gap: 8, alignItems: 'center' }}>
      <option.Icon style={{ borderRadius: 100, width: 22, height: 22 }} />
      {option.label}
    </AppFlexbox>
    {checked && <Check color="#868e96" size={15} />}
  </AppFlexbox>
);

const SocialShareModel = ({
  mediaQueries,
  editModeEnabled,
  baseStoreUrl,
  pathname,
  onNavigate,
  ecomStoreTheme,
  themeConfig
}) => {
  const { state: helperState, toggleWebstoreSocialSharingModel } = useContext(
    HelperContext
  );
  const [formState, setFormState] = useState({
    selectedPlatform: SOCIAL_SHARING_LIST[0].value,
    shareCurrentPage: false
  });
  const { isTabletOrSmaller } = mediaQueries;

  const shareImageUrl =
    themeConfig.socialShareImage ?? ecomStoreTheme.ecomStore.logoImageUrl;
  const shareUrl = formState.shareCurrentPage
    ? `${STORE_HOSTING_URL}${baseStoreUrl}${pathname}`
    : helperState.webstore.socialSharingModel.shareUrl ||
      `${STORE_HOSTING_URL}${baseStoreUrl}`;

  const shareOptions = SOCIAL_SHARING_LIST.filter(
    (f) => themeConfig[f.webstoreConfigValue] === 'true'
  );

  useEffect(() => {
    if (helperState.webstore.socialSharingModel.isOpen) {
      setFormState({
        selectedPlatform: SOCIAL_SHARING_LIST[0].value,
        shareCurrentPage: false
      });
    }
  }, [helperState.webstore.socialSharingModel.isOpen]);

  const onClose = () => {
    toggleWebstoreSocialSharingModel({
      ...helperState.webstore.socialSharingModel,
      isOpen: false
    });
  };

  const onCopyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    triggerNotification('Link copied to clipboard', 'success');
  };

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      editModeEnabled={editModeEnabled}
      formSectionProps={
        isTabletOrSmaller
          ? null
          : {
              onSubmit: () => {
                document
                  .getElementById(
                    `social-share-model-button-${formState.selectedPlatform}`
                  )
                  .click();
                onClose();
              },
              submitTitle: 'Continue',
              submitColor: 'blue',
              isCancelHidden: true
            }
      }
      isOpen={helperState.webstore.socialSharingModel.isOpen}
      mediaQueries={mediaQueries}
      onClose={onClose}
      radius={0}
      size={600}
      title="Share to Social Media"
      trapFocus={false}
      zIndex={201}
    >
      {isTabletOrSmaller ? (
        <AppStack style={{ gap: 16 }}>
          {!helperState.webstore.socialSharingModel.isResourceUrl && (
            <>
              <Checkbox
                checked={formState.shareCurrentPage}
                description="When enabled, this will share the current page URL instead of the homepage URL."
                label="Share current page"
                onChange={() =>
                  setFormState({
                    ...formState,
                    shareCurrentPage: !formState.shareCurrentPage
                  })
                }
                styles={{ label: { fontWeight: 500 } }}
              />
              <Divider />
            </>
          )}
          <AppFlexbox
            style={{
              gap: 16,
              width: '100%',
              maxWidth: '100%',
              overflow: 'auto',
              padding: '0px 0px 8px 0px',
              alignItems: 'start',
              justifyContent: 'start'
            }}
          >
            {shareOptions
              .slice(0, 4)
              .map(({ value, name, Icon, ShareComponent }) => (
                <ShareComponent
                  key={value}
                  media={shareImageUrl}
                  url={shareUrl}
                >
                  <AppStack
                    style={{
                      flex: 1,
                      gap: 5,
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Icon
                      style={{ borderRadius: 100, width: 46, height: 46 }}
                    />
                    <AppText style={{ fontSize: 11 }}>{name}</AppText>
                  </AppStack>
                </ShareComponent>
              ))}

            <AppStack
              onClick={onCopyToClipboard}
              style={{
                flex: 1,
                gap: 5,
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <AppStack
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'dodgerblue',
                  borderRadius: 100,
                  width: 46,
                  height: 46
                }}
              >
                <LinkIcon
                  style={{
                    color: '#FFF',
                    width: 32,
                    height: 32
                  }}
                />
              </AppStack>
              <AppText style={{ fontSize: 11, textAlign: 'center' }}>
                Copy link
              </AppText>
            </AppStack>

            {shareOptions
              .slice(4, shareOptions.length)
              .map(({ value, name, Icon, ShareComponent }) => (
                <ShareComponent
                  key={value}
                  media={shareImageUrl}
                  url={shareUrl}
                >
                  <AppStack
                    style={{
                      flex: 1,
                      gap: 5,
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Icon
                      style={{ borderRadius: 100, width: 46, height: 46 }}
                    />
                    <AppText style={{ fontSize: 11 }}>{name}</AppText>
                  </AppStack>
                </ShareComponent>
              ))}
          </AppFlexbox>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16 }}>
          <Select
            data={shareOptions.map((op) => ({
              label: op.name,
              value: op.value,
              Icon: op.Icon
            }))}
            label="Select platform"
            onChange={(value) =>
              setFormState({ ...formState, selectedPlatform: value })
            }
            renderOption={renderSelectOption}
            value={formState.selectedPlatform}
          />

          {!helperState.webstore.socialSharingModel.isResourceUrl && (
            <Checkbox
              checked={formState.shareCurrentPage}
              description="When enabled, this will share the current page URL instead of the homepage URL."
              label="Share current page"
              onChange={() =>
                setFormState({
                  ...formState,
                  shareCurrentPage: !formState.shareCurrentPage
                })
              }
              styles={{ label: { fontWeight: 500 } }}
            />
          )}

          <Divider label="OR" labelPosition="center" />

          <AppFlexbox
            style={{
              flex: 1,
              gap: 0,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <TextInput
              disabled
              onChange={() => {}}
              style={{
                flex: 1,
                height: 36,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
              value={shareUrl}
            />
            <Button
              color="dark"
              onClick={onCopyToClipboard}
              style={{
                height: 36,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }}
            >
              Copy Link
            </Button>
          </AppFlexbox>

          <>
            {shareOptions.map(({ value, name, Icon, ShareComponent }) => (
              <ShareComponent
                key={value}
                id={`social-share-model-button-${value}`}
                media={shareImageUrl}
                style={{ display: 'none' }}
                url={shareUrl}
              >
                <AppFlexbox
                  style={{
                    flex: 1,
                    gap: 5,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Icon />
                  <AppText style={{ fontSize: 14 }}>Share to {name}</AppText>
                </AppFlexbox>
              </ShareComponent>
            ))}
          </>
        </AppStack>
      )}
    </ResponsiveWebstoreModal>
  );
};

SocialShareModel.propTypes = {
  baseStoreUrl: PropTypes.string,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  themeConfig: PropTypes.object,
  viewPortAdjustment: PropTypes.string
};

export default SocialShareModel;
